import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { currentWeekStart, addDays, ddmmyyyy } from "@/lib/time";
import { Users, Coffee, AlertTriangle, ClipboardCheck, ChevronLeft, ChevronRight } from "lucide-react";

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <Card className="p-5 rounded-xl border-border">
      <div className="flex items-center gap-3">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-heading font-bold leading-none">{value}</p>
          <p className="text-sm text-muted-foreground mt-1">{label}</p>
        </div>
      </div>
    </Card>
  );
}

export default function AdminDashboard() {
  const [week, setWeek] = useState(currentWeekStart());
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const res = await api.get("/admin/dashboard", { params: { week_start: week } });
    setData(res.data);
  }, [week]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeek(addDays(week, -7))} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium px-2" data-testid="week-label">
            {data.week_start_display} – {data.week_end_display}
          </span>
          <Button variant="outline" size="icon" onClick={() => setWeek(addDays(week, 7))} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Clocked in now" value={data.clocked_in.length} tone="bg-[hsl(150,40%,90%)] text-[hsl(150,45%,24%)]" />
        <Stat icon={Coffee} label="On break" value={data.on_break.length} tone="bg-[hsl(33,70%,90%)] text-[hsl(33,65%,34%)]" />
        <Stat icon={AlertTriangle} label="Incomplete shifts" value={data.incomplete.length} tone="bg-[hsl(8,60%,92%)] text-[hsl(8,54%,38%)]" />
        <Stat icon={ClipboardCheck} label="Awaiting review" value={data.awaiting_review_count} tone="bg-[hsl(200,50%,90%)] text-[hsl(200,55%,30%)]" />
      </div>

      {data.ready_for_approval_count > 0 && (
        <Card className="p-4 rounded-xl border-[hsl(150,35%,76%)] bg-[hsl(150,40%,94%)]" data-testid="approval-reminder">
          <p className="text-sm text-[hsl(150,45%,24%)]">
            <span className="font-semibold">{data.ready_for_approval_count}</span> staff week{data.ready_for_approval_count > 1 ? "s are" : " is"} finished and waiting for your approval — look for the <span className="font-medium">Ready</span> tag below.
          </p>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 rounded-xl border-border">
          <h2 className="font-heading text-lg font-semibold mb-3">Currently clocked in</h2>
          {data.clocked_in.length === 0 && <p className="text-sm text-muted-foreground">Nobody is clocked in.</p>}
          <div className="space-y-2" data-testid="clocked-in-list">
            {data.clocked_in.map((s) => (
              <div key={s.shift_id} className="flex justify-between text-sm border-b border-border pb-2">
                <span className="font-medium">{s.staff_name}</span>
                <span className="text-muted-foreground">since {s.since}</span>
              </div>
            ))}
          </div>
          {data.on_break.length > 0 && (
            <>
              <h3 className="font-heading text-sm font-semibold mt-4 mb-2 text-muted-foreground uppercase tracking-wider">On break</h3>
              <div className="space-y-2" data-testid="on-break-list">
                {data.on_break.map((s) => (
                  <div key={s.shift_id} className="flex justify-between text-sm">
                    <span className="font-medium">{s.staff_name}</span>
                    <span className="text-[hsl(33,65%,40%)]">on break</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card className="p-5 rounded-xl border-border">
          <h2 className="font-heading text-lg font-semibold mb-3">Incomplete shifts (missing clock-out)</h2>
          {data.incomplete.length === 0 && <p className="text-sm text-muted-foreground">None — everyone clocked out.</p>}
          <div className="space-y-2" data-testid="incomplete-list">
            {data.incomplete.map((s) => (
              <div key={s.id} className="flex justify-between text-sm border-b border-border pb-2">
                <span className="font-medium">{s.staff_name}</span>
                <span className="text-muted-foreground">{s.date_display} • in {s.clock_in_display}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="rounded-xl border-border overflow-hidden">
        <div className="p-5 pb-0">
          <h2 className="font-heading text-lg font-semibold">Weekly hours by employee</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Staff</TableHead>
              <TableHead>Shifts</TableHead>
              <TableHead>Total hours</TableHead>
              <TableHead>Approved</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="weekly-totals">
            {data.weekly_totals.map((t) => (
              <TableRow key={t.user_id} className={t.active_account ? "" : "opacity-50"}>
                <TableCell className="font-medium">{t.staff_name}</TableCell>
                <TableCell>{t.shifts}</TableCell>
                <TableCell className="font-semibold">{t.total_display}</TableCell>
                <TableCell>{t.approved ? <span className="text-[hsl(150,45%,30%)] font-medium">Approved</span> : t.ready_for_approval ? <span className="inline-flex items-center rounded-full bg-[hsl(150,40%,90%)] text-[hsl(150,45%,26%)] px-2 py-0.5 text-xs font-medium" data-testid={`ready-tag-${t.user_id}`}>Ready</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/staff/${t.user_id}?week=${week}`)} data-testid={`view-staff-${t.user_id}`}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
